import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  Output,
  QueryList,
  TemplateRef,
} from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";

import {
  MedExpandEvent,
  MedTableDataRow,
  MedTableSettings,
  MedTableSortEvent,
  MedTableColumnConfig,
} from "./types";
import { ROW_EXPANSION } from "./constants/prefixes";
import { EXPAND_KEY } from "./constants/EXPAND_KEY";
import { MedTableSettingsLocal, MedTableTemplates } from "./types.local";
import { MedTemplateDirective } from "../../directives/med-template.directive";
import { MED_TABLE_DEFAULT_SETTING } from "./constants/medTableDefaultSettings";
import { findLastIndex } from "lodash";

@Component({
  selector: "med-table[data]",
  templateUrl: "./med-table.component.html",
  styleUrls: ["./med-table.component.scss"],
})
export class MedTableComponent<DataType extends MedTableDataRow> implements AfterViewInit {
  constructor(private cdRef: ChangeDetectorRef) { }

  @Input() settings!: MedTableSettings;
  @Input() columns: BehaviorSubject<MedTableColumnConfig[]> = new BehaviorSubject<MedTableColumnConfig[]>([]);

  @ContentChildren(MedTemplateDirective) templates!: QueryList<MedTemplateDirective>;

  @Output() expandChange = new EventEmitter<MedExpandEvent<DataType>>();
  @Output() sortChange = new EventEmitter<MedTableSortEvent | null>();

  expandTemplate?: TemplateRef<any>;
  templatesLocal: MedTableTemplates = {};
  sortLocal: MedTableSortEvent | null = null;
  data$: BehaviorSubject<DataType[]> = new BehaviorSubject<DataType[]>([]);

  get settingsLocal(): MedTableSettingsLocal {
    return Object.assign({}, MED_TABLE_DEFAULT_SETTING, this.settings);
  }

  @Input() set data(value: BehaviorSubject<DataType[]> | Observable<DataType[]>) {
    value.subscribe(list => this.data$.next(list.map(row => ({
      ...row,
      [EXPAND_KEY]: false,
    }))));
  }

  @Input() set sort(value: MedTableSortEvent | null) {
    this.sortLocal = value;
  }

  onSortChange(sort: MedTableSortEvent | null): void {
    this.sortLocal = sort;
    this.sortChange.emit(sort);
  }

  ngAfterViewInit(): void {
    this._initTemplates();
    this.cdRef.detectChanges();
  }

  expandToggle(index: number): void {
    const list = this.data$.getValue().map((row, i) => {
      if (i !== index) {
        return row;
      }

      this.expandChange.emit({
        row,
        index,
        visible: !row[EXPAND_KEY],
      });

      return {
        ...row,
        [EXPAND_KEY]: !row[EXPAND_KEY],
      };
    });
    this.data$.next(list);
  }

  isVisibleStickyDelimiter(index: number): boolean {
    const visibleStickyDelimiter = this.settings.visibleStickyDelimiter ?? false;
    const lastStickyColIndex = findLastIndex(this.columns.getValue(), item => item.fixed ?? false);
    return visibleStickyDelimiter &&  lastStickyColIndex === index;
  }

  private _initTemplates(): void {
    (this.templates || []).forEach(item => {
      if (item.getType() === ROW_EXPANSION) {
        this.expandTemplate = item.template;
        return;
      }

      this.templatesLocal[item.getType()] = item.template;
    });
  }
}
